import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, MarkEmailRead as MarkEmailReadIcon } from '@mui/icons-material';
import { resendVerificationEmail } from '../../api/authApi';
import { useAuth } from '../../auth/AuthContext';

const POLL_INTERVAL_MS = 6000;
const RESEND_COOLDOWN_S = 30;

const EmailVerificationGate = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const [resendStatus, setResendStatus] = useState('idle'); // idle | sending | sent | error
  const [cooldown, setCooldown] = useState(0);
  const [checking, setChecking] = useState(false);
  const [verified, setVerified] = useState(false);

  const shouldShow = user && !user.email_verified;

  // Auto-poll: check if the user verified in another tab
  useEffect(() => {
    if (!shouldShow) return;
    const interval = setInterval(async () => {
      const next = await refreshUser();
      if (next?.email_verified) setVerified(true);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [shouldShow, refreshUser]);

  // Resend cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    setResendStatus('sending');
    try {
      await resendVerificationEmail();
      setResendStatus('sent');
      setCooldown(RESEND_COOLDOWN_S);
    } catch {
      setResendStatus('error');
    }
  };

  const handleCheckNow = async () => {
    setChecking(true);
    const next = await refreshUser();
    setChecking(false);
    if (next?.email_verified) setVerified(true);
  };

  if (!shouldShow && !verified) return null;

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      PaperProps={{ elevation: 8, sx: { borderRadius: 3 } }}
    >
      <DialogContent sx={{ p: { xs: 3, sm: 5 } }}>
        {verified ? (
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
            <Typography variant="h4" fontWeight={800}>
              Email verified
            </Typography>
            <Typography color="text.secondary">
              Your email has been confirmed. You're all set.
            </Typography>
            <Button variant="contained" size="large" onClick={() => setVerified(false)}>
              Continue to Qode Clarity
            </Button>
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Stack spacing={1.5} alignItems="center" textAlign="center">
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MarkEmailReadIcon sx={{ fontSize: 36, color: 'primary.main' }} />
              </Box>
              <Typography variant="h4" fontWeight={800}>
                Verify your email
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 400 }}>
                We sent a verification link to{' '}
                <strong>{user?.email}</strong>.
                <br />
                Open that email and click the link before you continue.
              </Typography>
            </Stack>

            <Alert severity="info" icon={false} sx={{ fontSize: 13 }}>
              Can't find it? Check your spam or junk folder. The link expires after 24 hours.
            </Alert>

            <Stack spacing={1.5}>
              <Button
                variant="contained"
                size="large"
                onClick={handleCheckNow}
                disabled={checking}
                startIcon={checking ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {checking ? 'Checking…' : "I've verified — let me in"}
              </Button>

              <Divider>or</Divider>

              <Button
                variant="outlined"
                size="large"
                onClick={handleResend}
                disabled={resendStatus === 'sending' || cooldown > 0}
              >
                {resendStatus === 'sending'
                  ? 'Sending…'
                  : cooldown > 0
                  ? `Resend in ${cooldown}s`
                  : 'Resend verification email'}
              </Button>

              <Button
                variant="text"
                size="large"
                onClick={() => navigate('/')}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Go to home
              </Button>

              {resendStatus === 'sent' && (
                <Alert severity="success">Verification email sent. Check your inbox.</Alert>
              )}
              {resendStatus === 'error' && (
                <Alert severity="error">Could not send the email. Try again in a moment.</Alert>
              )}
            </Stack>

            <Typography variant="caption" color="text.disabled" textAlign="center">
              This page checks automatically every few seconds.
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailVerificationGate;
